import React, { useEffect, useMemo, useRef } from "react"

import { Table, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import Button from "../Button"
import Private from "../Private"

//import EmptyComponent from '../Empty'

import "./styles.less"

const { Text } = Typography

export const itemRender = (_, type, originalElement, t) => {
  if (type === "prev") {
    return <Button className="ant-pagination-item-link1">{t("Previous")}</Button>
  }
  if (type === "next") {
    return <Button className="ant-pagination-item-link1">{t("Next")}</Button>
  }
  return originalElement
}

const TableComponent = ({
  loading,
  dataSource,
  pagination,
  withOutPagination,
  showSizeChanger,
  rowKey,
  onScroll,
  next,
  hoverMarker,
  emptyType,
  className,
  EmptyComponent,
  scroll,
  headerDataTour,
  columns,
  testIdPrefix,
  onRow,
  ...props
}) => {
  const { t } = useTranslation()
  const tableRef = useRef(null)

  // A column opts in with `sensitive: true` instead of every column.js hand-wrapping its own
  // render in <Private> - untouched for any column without the flag, so this is purely additive.
  const processedColumns = useMemo(
    () =>
      columns
        ?.map((col) =>
          !col?.sensitive
            ? col
            : {
                ...col,
                render: (value, record, index) => (
                  <Private>{col.render ? col.render(value, record, index) : value}</Private>
                )
              }
        )
        .map((col) => {
          if (!testIdPrefix) return col
          const key = col?.key ?? [].concat(col?.dataIndex ?? []).join(".")
          return {
            ...col,
            onHeaderCell: (column) => ({
              ...col.onHeaderCell?.(column),
              "data-testid": `${testIdPrefix}-header-${key}`
            }),
            onCell: (record, index) => ({
              ...col.onCell?.(record, index),
              "data-testid": `${testIdPrefix}-cell-${key}`
            })
          }
        }),
    [columns, testIdPrefix]
  )

  const getRowKey = (record) => record[rowKey || "id"]

  useEffect(() => {
    if (!onScroll) return
    const tableElement = tableRef?.current?.nativeElement
    let tableBody = null
    if (tableElement) {
      tableBody = tableElement.querySelector(".ant-table-body")
      if (tableBody && next) {
        tableBody.addEventListener("scroll", onScroll)
      }
    }
    return () => {
      if (tableBody) tableBody.removeEventListener("scroll", onScroll)
    }
  }, [tableRef, next])

  useEffect(() => {
    if (!headerDataTour) return
    const tableElement = tableRef?.current?.nativeElement
    const headerElement =
      tableElement?.querySelector(".ant-table-header") ||
      tableElement?.querySelector(".ant-table-thead")
    headerElement?.setAttribute("data-tour", headerDataTour)
  }, [tableRef, headerDataTour, columns])

  useEffect(() => {
    if (hoverMarker) {
      tableRef?.current?.scrollTo({ key: hoverMarker })
    }
  }, [hoverMarker])

  // A marker instead of a data-testid on the Spin: a nested Spin copies its props onto a wrapper
  // that stays mounted, so the id would never disappear once loading finishes.
  const loadingMarker = testIdPrefix && loading && (
    <span hidden data-testid={`${testIdPrefix}-table-loading`} />
  )

  return (
    <>
      {loadingMarker}
      <Table
        ref={tableRef}
        id="custom-table"
        className={cls("custom-table", className)}
        rowKey={getRowKey}
        loading={loading}
        dataSource={dataSource}
        columns={processedColumns}
        showSorterTooltip={false}
        rowHoverable={false}
        // scroll={{x: 1300}}
        scroll={scroll ? scroll : { x: 1300 }}
        pagination={
          !dataSource?.length || withOutPagination
            ? false
            : {
                position: ["none", "bottomRight"],
                showLessItems: true,
                showSizeChanger: showSizeChanger,
                //pageSizeOptions: [1,2,3],
                itemRender: (_, type, originalElement) =>
                  pagination?.pageSize >= pagination?.total
                    ? null
                    : itemRender(_, type, originalElement, t),
                showTotal: (total, range) => (
                  <Text>
                    Showing <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text> results
                  </Text>
                ),
                ...pagination
              }
        }
        locale={EmptyComponent ? { emptyText: <EmptyComponent /> } : undefined}
        onRow={
          testIdPrefix
            ? (record, index) => ({
                "data-testid": `${testIdPrefix}-row-${getRowKey(record)}`,
                ...onRow?.(record, index)
              })
            : onRow
        }
        {...props}
      />
    </>
  )
}

export default React.memo(TableComponent)
